"use client";

import { useMemo, type ReactNode } from "react";
import Link from "next/link";
import { CandidateAvatar } from "@/components/ui/candidate-avatar";
import { Icon } from "@/components/ui/icon";
import { SocialNetworkIcon } from "@/components/ui/social-network-icon";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";
import {
  pickHighestDegree,
  selectTopEducation,
  type CandidateEducation as DisplayEducation,
  type CandidateEducationInput,
} from "@/features/candidate/lib/education";
import { buildSocialLinks } from "@/features/candidate/lib/social-links";
import { readExperiences } from "@/features/candidate/lib/profile-section-data";
import { selectCurrentJob } from "@/features/candidate/lib/select-current-job";
import { CompanyLogo, EducationLogo } from "@/features/candidate";
import { useProfile } from "@/features/candidate";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";

export interface ProfileHeaderProps {
  /** Right-side desktop actions (e.g. Add to Job, Export). */
  actions?: ReactNode;
  /** Mobile-only quick action bar shown below the avatar/name card. */
  mobileActions?: ReactNode;
  /** Items to render in the mobile overflow dropdown menu. If omitted, the dropdown is hidden. */
  mobileMenuItems?: ReactNode;
  /** Optional badge or chip rendered inline next to the name (e.g. stage badge). */
  nameBadge?: ReactNode;
  /** Optional extra rows rendered below the standard location/currentJob/topEducation rows
   * (e.g. Profile Origin / Added At / Job Requisition meta in pipeline view). */
  extraRows?: ReactNode;
}

/**
 * Shared candidate profile header - avatar, name, social links, current job,
 * top education. Reads `profile` from the `useProfile()` context that
 * `Profile.Layout` provides.
 *
 * Host-agnostic: callers compose actions, badges, and extra meta via slots.
 */
export function ProfileHeader({
  actions,
  mobileActions,
  mobileMenuItems,
  nameBadge,
  extraRows,
}: ProfileHeaderProps) {
  const { profile } = useProfile();

  const socialLinks = useMemo(() => buildSocialLinks(profile), [profile]);

  const currentJob = useMemo(() => {
    if (!profile) return null;
    return selectCurrentJob(readExperiences(profile));
  }, [profile]);

  const topEducation = useMemo<DisplayEducation | null>(() => {
    if (!profile) return null;
    const edu = profile.education as CandidateEducationInput[] | undefined;
    return selectTopEducation(edu) ?? null;
  }, [profile]);

  if (!profile) return null;
  const avatarName =
    profile.full_name ||
    [profile.first_name, profile.last_name].filter(Boolean).join(" ") ||
    "";

  return (
    <>
      <div className="flex w-full shrink-0 flex-col gap-3 border-b border-solid border-border bg-surface-header px-4 py-4 md:flex-row md:items-center md:justify-between md:px-8 md:py-6">
        <div className="flex items-center gap-3 md:gap-5">
          <div className="md:hidden shrink-0">
            <CandidateAvatar
              name={avatarName}
              avatarUrl={profile.avatar_url}
              size={44}
              initialsPx={15}
              className="shadow-[0px_2px_4px_0px_rgba(0,0,0,0.05)]"
            />
          </div>
          <div className="hidden md:block shrink-0">
            <CandidateAvatar
              name={avatarName}
              avatarUrl={profile.avatar_url}
              size={90}
              initialsPx={32}
              className="shadow-[0px_2.25px_4.5px_0px_rgba(0,0,0,0.05)]"
            />
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-base font-medium text-foreground whitespace-nowrap md:text-lg">
                {profile.full_name ?? ""}
              </p>
              {socialLinks.length > 0 && (
                <div className="flex items-center gap-2.5">
                  {socialLinks.map((link) => (
                    <Link
                      key={link.label}
                      href={link.url}
                      target="_blank"
                      className="hover:opacity-50 duration-200"
                      rel="noopener noreferrer"
                      aria-label={link.label}
                    >
                      {link.network ? (
                        <SocialNetworkIcon
                          network={link.network}
                          className="size-3.5"
                        />
                      ) : (
                        <Icon
                          name="IconGlobe"
                          size={14}
                          className="text-foreground/60"
                        />
                      )}
                    </Link>
                  ))}
                </div>
              )}
              {nameBadge}
              {mobileMenuItems ? (
                <div className="ml-auto md:hidden">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex size-7 items-center justify-center rounded-[6px] bg-contrast/4 border border-contrast/[0.15] hover:bg-contrast/8 transition-colors cursor-pointer shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                      >
                        <Icon
                          name="IconDotGrid1x3Horizontal"
                          size={16}
                          className="text-foreground"
                        />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" sideOffset={6}>
                      {mobileMenuItems}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : null}
            </div>
            {profile.location_name && (
              <p className="truncate text-[13px] font-normal leading-normal text-foreground/75">
                {titleCasePreserveAcronyms(profile.location_name)}
              </p>
            )}
            {currentJob &&
              (() => {
                const companyName = currentJob.company_name?.trim() ?? "";
                const hasCompany = companyName.length > 0;
                return (
                  <div className="flex w-full items-center gap-1.5">
                    <CompanyLogo
                      size="sm"
                      domain={currentJob.company_domain}
                      name={hasCompany ? companyName : null}
                    />
                    <p className="text-[13px] font-normal leading-normal">
                      <span className="text-foreground">
                        {titleCasePreserveAcronyms(currentJob.title_name ?? "")}
                      </span>
                      {hasCompany && (
                        <span className="text-foreground/70">
                          {" "}
                          at {titleCasePreserveAcronyms(companyName)}
                        </span>
                      )}
                    </p>
                  </div>
                );
              })()}
            {topEducation &&
              (() => {
                const schoolName = topEducation.schoolName;
                if (!schoolName) return null;
                const highestDegree =
                  pickHighestDegree(topEducation.source.degrees) ??
                  topEducation.degree;
                const field = topEducation.field;
                const titleText = [highestDegree, field]
                  .filter(Boolean)
                  .map((s) => titleCasePreserveAcronyms(s!))
                  .join(", ");
                return (
                  <div className="flex w-full items-center gap-1.5">
                    <EducationLogo
                      size="sm"
                      domain={topEducation.schoolDomain}
                      name={schoolName}
                    />
                    <p className="text-[13px] font-normal leading-normal">
                      {titleText ? (
                        <>
                          <span className="text-foreground">{titleText}</span>
                          <span className="text-foreground/70">
                            {" "}
                            at {titleCasePreserveAcronyms(schoolName)}
                          </span>
                        </>
                      ) : (
                        <span className="text-foreground">
                          {titleCasePreserveAcronyms(schoolName)}
                        </span>
                      )}
                    </p>
                  </div>
                );
              })()}
            {extraRows}
          </div>
        </div>

        {actions}
      </div>

      {mobileActions ? (
        <div className="flex md:hidden shrink-0 items-center gap-2 border-b border-border bg-surface-header px-4 py-3">
          {mobileActions}
        </div>
      ) : null}
    </>
  );
}
